package com.yogaaicoach.pages.trainingprogram

import com.yogaaicoach.pages.dashboard.mainGoalDisplayName
import com.yogaaicoach.scope
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.ChildrenBuilder
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

internal external interface UpdateTrainingMainPageProps : Props {
    var viewModel: UpdateTrainingViewModel
    var onEditGoals: () -> Unit
    var onEditLevel: () -> Unit
    var onEditAgeGroup: () -> Unit
    var onEditSchedule: () -> Unit
    var onSaveDone: () -> Unit
}

private fun LevelKey.displayName(): String = when (this) {
    LevelKey.ABSOLUTE_BEGINNER -> "Absolute Beginner"
    LevelKey.ADVANCED_BEGINNER -> "Advanced Beginner"
    LevelKey.INTERMEDIATE -> "Intermediate"
    LevelKey.ADVANCED_INTERMEDIATE -> "Advanced Intermediate"
    LevelKey.ADVANCED -> "Advanced"
}

private fun AgeGroupKey.displayName(): String = when (this) {
    AgeGroupKey.UNDER_30 -> "Under 30"
    AgeGroupKey.AGE_30_39 -> "30 - 39"
    AgeGroupKey.AGE_40_49 -> "40 - 49"
    AgeGroupKey.AGE_50_64 -> "50 - 64"
    AgeGroupKey.OVER_65 -> "65 +"
}

private fun Weekday.shortTitle(): String = when (this) {
    Weekday.Monday -> "Mon"
    Weekday.Tuesday -> "Tue"
    Weekday.Wednesday -> "Wed"
    Weekday.Thursday -> "Thu"
    Weekday.Friday -> "Fri"
    Weekday.Saturday -> "Sat"
    Weekday.Sunday -> "Sun"
}

internal val UpdateTrainingMainPage = FC<UpdateTrainingMainPageProps> { props ->
    val vm = props.viewModel
    var selectedGoals by useState(vm.selectedGoals.value)
    var selectedLevel by useState(vm.selectedLevel.value)
    var selectedAgeGroup by useState(vm.selectedAgeGroup.value)
    var selectedSchedule by useState(vm.selectedSchedule.value)
    var hasChanges by useState(vm.hasChanges.value)
    var isSaving by useState(vm.isSaving.value)

    useEffectOnce {
        scope.launch { vm.selectedGoals.collect { selectedGoals = it } }
        scope.launch { vm.selectedLevel.collect { selectedLevel = it } }
        scope.launch { vm.selectedAgeGroup.collect { selectedAgeGroup = it } }
        scope.launch { vm.selectedSchedule.collect { selectedSchedule = it } }
        scope.launch { vm.hasChanges.collect { hasChanges = it } }
        scope.launch { vm.isSaving.collect { isSaving = it } }
    }

    div {
        className = ClassName("update-training-content")

        UpdateSectionCard("Schedule", props.onEditSchedule) {
            if (selectedSchedule.isEmpty()) {
                p { className = ClassName("update-training-empty"); +"No days selected" }
            } else {
                selectedSchedule.forEach { day ->
                    div {
                        className = ClassName("update-training-schedule-row")
                        span { className = ClassName("update-training-schedule-day"); +day.day.shortTitle() }
                        span { className = ClassName("update-training-schedule-duration"); +"${day.duration.minutes} min" }
                    }
                }
            }
        }

        UpdateSectionCard("Goals", props.onEditGoals) {
            if (selectedGoals.isEmpty()) {
                p { className = ClassName("update-training-empty"); +"None selected" }
            } else {
                selectedGoals.forEach { goal ->
                    p { className = ClassName("update-training-chip"); +mainGoalDisplayName(goal.name) }
                }
            }
        }

        UpdateSectionCard("Level", props.onEditLevel) {
            p {
                className = ClassName("update-training-chip")
                +(selectedLevel?.displayName() ?: "None selected")
            }
        }

        UpdateSectionCard("Age Group", props.onEditAgeGroup) {
            p {
                className = ClassName("update-training-chip")
                +(selectedAgeGroup?.displayName() ?: "None selected")
            }
        }
    }

    div {
        className = ClassName("update-training-footer")
        button {
            className = ClassName(if (isSaving) "primary-button loading" else "primary-button")
            disabled = !hasChanges || isSaving
            onClick = { vm.saveChanges(props.onSaveDone) }
            if (isSaving) {
                span { className = ClassName("button-spinner") }
            } else {
                span { +"Save Changes" }
            }
        }
    }
}

private fun ChildrenBuilder.UpdateSectionCard(
    title: String,
    onEdit: () -> Unit,
    content: ChildrenBuilder.() -> Unit,
) {
    div {
        className = ClassName("update-training-card")
        div {
            className = ClassName("update-training-card-header")
            h2 { className = ClassName("update-training-card-title"); +title }
            button {
                className = ClassName("text-button")
                onClick = { onEdit() }
                +"Edit"
            }
        }
        div {
            className = ClassName("update-training-card-body")
            content()
        }
    }
}
